import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import DefaultImage from './default-image';

const propTypes = {
	alt: PropTypes.string.isRequired,
	src: PropTypes.string,
	dataTestId: PropTypes.string,
	isAltReal: PropTypes.bool,
	lazy: PropTypes.bool,
	border: PropTypes.string,
	fetchPriority: PropTypes.oneOf(['low', 'auto', 'high']),
	sources: PropTypes.arrayOf(
		PropTypes.shape({
			media: PropTypes.string,
			srcsetList: PropTypes.arrayOf(
				PropTypes.shape({
					template: PropTypes.string,
					scale: PropTypes.string,
				}),
			),
		}),
	),
};
const defaultProps = {
	dataTestId: null,
	isAltReal: false,
	src: '',
	lazy: false,
	border: null,
	sources: [],
	fetchPriority: null,
};

const StyledPicture = styled.picture`
	position: relative;
	width: 100%;
	padding-top: 133.33%;
`;

const isExist = (arr) => arr && Array.isArray(arr) && arr.length > 0;
const hasTemplate = ({ template }) => template && typeof template === 'string';
const isMultiSrc = (srcs) => srcs && srcs.length && srcs.every(hasTemplate);
const defaultBorderStyle = 'var(--border-width-xs) solid var(--color-global-grey300)';

export const ProductImage = React.forwardRef(
	({ alt, src, isAltReal, border, dataTestId, sources, lazy, fetchPriority, ...props }, ref) => {
		const borderStyle = border || defaultBorderStyle;
		const styles = useMemo(
			() => ({
				display: 'block',
				width: '100%',
				objectFit: 'cover',
				boxSizing: 'border-box',
				position: 'absolute',
				top: 0,
				left: 0,
				height: '100%',
				border: borderStyle,
			}),
			[borderStyle],
		);

		const imgSrc = useMemo(() => (src.includes('?') ? `${src}&` : `${src}?`), [src]);

		const buildImageSrc = useCallback(
			(params) => {
				if (!params?.length) {
					return src;
				}

				return `${imgSrc}${params.join('&')}`;
			},
			[src, imgSrc],
		);

		const srcSets = useMemo(() => {
			const baseParams = ['fmt=auto'];

			if (!isExist(sources)) return [{ srcSet: buildImageSrc(baseParams) }];

			return sources.map(({ media, srcsetList }) => {
				if (!isMultiSrc(srcsetList)) {
					return buildImageSrc(baseParams);
				}

				const params = [...baseParams];
				return {
					media,
					srcSet: srcsetList
						.map(({ template, scale }) => {
							const paramsWithTpl = [`$${template}$`, ...params];
							const scaleStr = scale ? ` ${scale}` : '';

							return `${buildImageSrc(paramsWithTpl)}${scaleStr}`;
						})
						.join(', '),
				};
			});
		}, [buildImageSrc, sources]);

		if (!src) {
			return <DefaultImage alt={alt} isAltReal={isAltReal} {...props} />;
		}

		return (
			<StyledPicture className="fuse-product-image" {...props}>
				{srcSets.map((attrs, idx) => (
					<source key={attrs.media || idx} {...attrs} />
				))}
				<img
					src={`${imgSrc}fmt=auto`}
					alt={alt}
					ref={ref}
					className="fuse-product-image__img"
					loading={lazy ? 'lazy' : 'eager'}
					fetchpriority={fetchPriority || 'auto'}
					data-testid={dataTestId || 'fuse-product-image'}
					aria-hidden={isAltReal ? undefined : 'true'}
					style={styles}
					{...props}
				/>
			</StyledPicture>
		);
	},
);

ProductImage.propTypes = propTypes;
ProductImage.defaultProps = defaultProps;

ProductImage.displayName = 'ProductImage';

export default ProductImage;
